import {useCallback, useEffect, useMemo} from "react";
import classnames from "classnames";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {useSearchParam} from "react-use";

import {AnalyticsBadge} from "../../components/analytics-badge";
import {Badge} from "../../components/badge";
import {InputRow} from "../../components/input";
import {Icon} from "../../components/images";
import {UserAvatar, UserSpan} from "../../components/user-avatar";
import {P4, P, P2, Span5, Span4, Span2} from "../../components/text";
import {
	GET_USER_SHARE_EVENTS,
	GET_USER_EVENTS,
	SCHEDULE_SHARE_EVENT,
	SCHEDULE_QUEUE,
	useMyUser,
	ShareEvent,
} from "../../data";
import {GET_LEADER_BOARD, GET_USER_BADGES, LEVEL_XP, getUserRank} from "../../data/user";
import {useMutationToast, useToast} from "../../toast";
import {nFormatter} from "../../format";
import {defaultLoadingProps as loadingProps, usePaginatedQuery} from "../../paginated-query";

import styles from "./feed.module.scss";

const renderScheduledPost = ({id, share, scheduledFor}: ShareEvent, onItemClick: (id: number) => void) => (
	<div className={classnames("space", styles.scheduledPost)} key={id} onClick={() => onItemClick(share.id)}>
		<InputRow>
			{share.opengraphs.general.image ? (
				<img className="space" src={share.opengraphs.general.image} />
			) : (
				<img height={24} width={24} src="/svgs/textPost.svg" alt="textPost" className="space" />
			)}
			<P trim={3} bold className={styles.postComment}>
				{share.opengraphs.general.title ?? share.opengraphs.general.comment ?? ""}
			</P>
		</InputRow>
		<InputRow position="between" className={styles.postDetails}>
			<Span5
				className={classnames(styles.shareTitle, "space")}
				title={share.opengraphs.general.title}
				trim={1}
				onClick={e => e.stopPropagation()}
				href={share.url ?? undefined}
			>
				{share.url ? share.opengraphs.general.title : ""}
			</Span5>
			<P4 color="pink">{dayjs(scheduledFor).formatAs()}</P4>
		</InputRow>
	</div>
);
const empty = <P4 color="grey">You don’t have any upcoming scheduled posts.</P4>;
const variables = {sort: "CREATED"};

const MyHubPanel = () => {
	const me = useMyUser();
	const toast = useToast();
	const {data: userBadges} = useQuery(GET_USER_BADGES);
	const {data} = useQuery(GET_LEADER_BOARD);
	const navigate = useNavigate();
	const renderItem = useCallback(
		(post: ShareEvent) => renderScheduledPost(post, shareId => navigate(`/collections/posts/${shareId}`)),
		[navigate]
	);
	const {handleScroll, render, data: scheduledPosts} = usePaginatedQuery<ShareEvent>(GET_USER_SHARE_EVENTS, {
		loadingProps,
		variables,
		renderItem,
		empty,
	});

	const toBeSentPosts = useMemo(
		() =>
			scheduledPosts
				?.filter(share => !share.sharedAt && dayjs(share?.scheduledFor).isBefore())
				?.map(share => share.id) ?? [],
		[scheduledPosts]
	);
	const {stopPolling, startPolling} = useQuery(GET_USER_EVENTS, {
		variables: {ids: toBeSentPosts},
		skip: !toBeSentPosts?.length,
	});
	const [scheduleSharesByQueueUrlId] = useMutationToast(SCHEDULE_SHARE_EVENT);
	const [scheduleSharesByQueueId] = useMutationToast(SCHEDULE_QUEUE);
	const userLevel = useMemo(() => getUserRank(data?.leaderBoard?.experience), [
		data?.leaderBoard?.experience,
	]);
	const shareId = useSearchParam("shareId");
	const queueId = useSearchParam("queueId");

	const userLevelProgress = useMemo(() => {
		if (!data?.leaderBoard?.experience) return 0;
		if (userLevel === LEVEL_XP.length - 1) return 100;
		const progress =
			((data.leaderBoard.experience - LEVEL_XP[userLevel - 1]) /
				(LEVEL_XP[userLevel] - LEVEL_XP[userLevel - 1])) *
			100;
		if (progress <= 5) return 5;
		if (progress > 99) return 100;

		return progress;
	}, [userLevel, data?.leaderBoard?.experience]);
	const neededExp = useMemo(() => LEVEL_XP[userLevel] - data?.leaderBoard?.experience, [
		data?.leaderBoard?.experience,
		userLevel,
	]);

	const onCreatePost = useCallback(() => navigate("/collections/posts/new", {state: {type: "personal"}}), [
		navigate,
	]);

	useEffect(() => {
		if (shareId) {
			scheduleSharesByQueueUrlId({
				variables: {id: shareId, timezone: dayjs.tz.guess()},
				onCompleted: () => {
					toast({color: "green", text: "Your post has been scheduled!"});
				},
			}).then(() => {
				navigate({search: ""});
			});
		}
		if (queueId) {
			scheduleSharesByQueueId({
				variables: {id: queueId, timezone: dayjs.tz.guess()},
				onCompleted: () => {
					toast({color: "green", text: "Your posts have been scheduled!"});
				},
			}).then(() => {
				navigate({search: ""});
			});
		}
	}, [scheduleSharesByQueueUrlId, scheduleSharesByQueueId, navigate, toast, shareId, queueId]);

	useEffect(() => {
		if (toBeSentPosts.length === 0) {
			return stopPolling();
		}

		startPolling(5000);
	}, [startPolling, stopPolling, toBeSentPosts]);

	return (
		<div className={styles.panelContainer} onScroll={handleScroll}>
			<div className={classnames("space", styles.panel, styles.userPanel)}>
				<InputRow>
					<UserAvatar className="space" size="extraLarge" />
					<div className={classnames("space", styles.achievements)}>
						<InputRow position="between" className={styles.gap}>
							<UserSpan userId={me.id} size={2} />
							<div className={styles.badges}>
								{userBadges?.earnedBadges?.map((badge, index) => (
									<div key={badge.id} style={{zIndex: userBadges?.earnedBadges.length - index}}>
										<AnalyticsBadge size="nano" badge={badge} />
									</div>
								))}
							</div>
						</InputRow>
						<div className={styles.roleAndLevel}>
							{me?.role === "admin" && <Badge color="pink" text="Admin" />}
							<h6>Level {userLevel}</h6>
						</div>
					</div>
				</InputRow>
				<InputRow className={classnames(styles.stats, !me.org.options.leaderboard && styles.twoColumn)}>
					{me.org.options.leaderboard ? (
						<>
							<Span4 color="grey">Rank</Span4>
							<Span4 color="grey">Shares</Span4>
							<Span4 color="grey">Clicks</Span4>
							<Span4 color="grey">SocialScore</Span4>
							<Span2 bold>{data?.leaderBoard?.rank}</Span2>
							<Span2 bold>{data?.leaderBoard?.shares}</Span2>
							<Span2 bold>{data?.leaderBoard?.clicks}</Span2>
							<Span2 bold>{nFormatter(data?.leaderBoard?.smartScore, 1)}</Span2>
						</>
					) : (
						<>
							<Span4 color="grey">Shares</Span4>
							<Span4 color="grey">Clicks</Span4>
							<Span2 bold>{data?.leaderBoard?.shares}</Span2>
							<Span2 bold>{data?.leaderBoard?.clicks}</Span2>
						</>
					)}
				</InputRow>
				<div className="space">
					<P4>
						{userLevel === LEVEL_XP.length ? (
							<strong>Max Level reached</strong>
						) : (
							<>
								<strong>{neededExp} Points</strong> away from <strong>Level {userLevel + 1}</strong>
							</>
						)}
					</P4>
					<div className={styles.progressFill}>
						<div className={styles.progress} style={{width: `${userLevelProgress}%`}} />
					</div>
					<InputRow position="right">
						<P4>
							See how to{" "}
							<a
								href="https://cvssupport.wpenginepowered.com/article/badges-and-achievements-in-clearview-social"
								target="_blank"
								rel="noreferrer"
							>
								Earn more points
							</a>
						</P4>
					</InputRow>
				</div>
			</div>
			<div onClick={onCreatePost} className={classnames("space", styles.panel, styles.createPost)}>
				<P2>Create a Post</P2>
				<Icon icon="create-post" />
			</div>

			<div className={classnames("space", styles.panel, styles.scheduledPostsContainer)}>
				<InputRow position="between">
					<P2>Scheduled Posts</P2>
					<a href="/schedule">See Calendar</a>
				</InputRow>
				{render}
			</div>
		</div>
	);
};

export default MyHubPanel;
